/**
 * svmApi - every network call the SVM checklist feature makes.
 *
 * Owns the two fetch helpers (window.fetch only - an interceptor attaches the
 * auth token; we NEVER set Authorization ourselves - and err.status is attached
 * so callers can branch on 403 / PM-lock), every /api/svm/* path builder, and
 * the module-level template cache.
 *
 * Endpoints: /api/svm/projects, /disciplines, /template, /checks, /check
 * (POST/DELETE), /zones (PUT), /history, /share (POST/GET/DELETE).
 */

// ── fetch helpers (interceptor attaches auth; never set Authorization) ──
export async function getJson(url) {
  const r = await fetch(url);
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    const err = new Error(e.error || `Request failed (${r.status})`);
    err.status = r.status; // callers can branch on 403 etc.
    throw err;
  }
  return r.json();
}
export async function sendJson(method, url, body) {
  const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    const err = new Error(e.error || `Request failed (${r.status})`);
    err.status = r.status; // callers can branch on 403 etc.
    throw err;
  }
  return r.json();
}

// ── API paths - the single place any /api/svm URL is spelled out ──
export const SVM_PROJECTS_URL = '/api/svm/projects';
export const SVM_CHECK_URL = '/api/svm/check'; // POST (sign off / N/A) · DELETE (untick)
export const SVM_ZONES_URL = '/api/svm/zones'; // PUT (zone scope)
export const SVM_SHARE_URL = '/api/svm/share'; // POST (create) · DELETE (revoke)
export const svmDisciplinesUrl = (projectLabel) => `/api/svm/disciplines?project=${encodeURIComponent(projectLabel)}`;
export const svmTemplateUrl = (disciplineId) => `/api/svm/template?discipline=${encodeURIComponent(disciplineId)}`;
export const svmChecksUrl = (projectLabel, disciplineId, zone) =>
  `/api/svm/checks?project=${encodeURIComponent(projectLabel)}&discipline=${encodeURIComponent(disciplineId)}&zone=${encodeURIComponent(zone)}`;
export const svmHistoryUrl = (projectLabel, disciplineId, zone, itemNo) =>
  `/api/svm/history?project=${encodeURIComponent(projectLabel)}&discipline=${encodeURIComponent(disciplineId)}&zone=${encodeURIComponent(zone)}&itemNo=${itemNo}`;
export const svmShareListUrl = (project) => `/api/svm/share?project=${encodeURIComponent(project)}`;
export const SVM_PRODUCT_DATA_URL = '/api/svm/product-data'; // POST · PUT /:id · DELETE /:id
export const svmProductDataUrl = (projectLabel, disciplineId, zone) =>
  `/api/svm/product-data?project=${encodeURIComponent(projectLabel)}&discipline=${encodeURIComponent(disciplineId)}&zone=${encodeURIComponent(zone)}`;

// ── template module-level cache: fetched ONCE per app session PER DISCIPLINE
// (each book is its own ~300KB template) ──
const templateCaches = new Map(); // disciplineId -> template
const templatePromises = new Map(); // disciplineId -> in-flight promise
export function cachedTemplate(disciplineId) {
  return templateCaches.get(disciplineId) || null;
}
export function loadTemplate(disciplineId) {
  if (templateCaches.has(disciplineId)) return Promise.resolve(templateCaches.get(disciplineId));
  if (!templatePromises.has(disciplineId)) {
    templatePromises.set(
      disciplineId,
      getJson(svmTemplateUrl(disciplineId))
        .then((d) => {
          templateCaches.set(disciplineId, d.template);
          return d.template;
        })
        .catch((err) => {
          templatePromises.delete(disciplineId);
          throw err;
        })
    );
  }
  return templatePromises.get(disciplineId);
}
